import React, {
  useContext, 
} from 'react';
import { StoreContext } from 'models/import';
import { CellProps } from 'material-react-table';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ValidationTableMatchResult, MatchElement, ValidationTableTransaction } from 'models/validation';
import Pill, { PillModifier } from 'components/pill';
import StatusIcon, { StatusIconModifier } from 'components/statusIcon';
import { StatusEnum } from 'models/general';
import {
  ValidationTableDef,
  Dictionary,
} from 'models/validation';
import ArrowRightIcon from 'assets/icons/arrowRight.svg';
import Btn, { BtnModifier } from 'components/btn';
import ValidationStatusTooltip, { hasItems, ValidationStatusTooltipKind } from './validationStatusTooltip';
import { getCoinString } from 'services/transactions';

interface ValidationStatusCellProps {
  cell: CellProps<ValidationTableMatchResult>;
  handleDuplicatesButtonPress: (id: number | null | undefined) => void;
  duplicatesCurrentlyHidden: boolean;
  warningsCurrentlyHidden: boolean;
  noticesCurrentlyHidden: boolean;
  isTrade: (original: ValidationTableDef) => boolean; // replace 'any' with the correct type
  deleteMatch: (pairId: number | null, transationId: number, userId: number, coin: string, fromTime: number, toTime: number) => void;
  updateMatch: (pairId: number, transationId: number, userId: number, coin: string, fromTime: number, toTime: number) => void;
  forceMatch: (pairId: number, transationId: number, deleteId: number, userId: number, coin: string, fromTime: number, toTime: number) => void;
  getSubRowPossibleMatches: (
    possibleMatches: ValidationTableMatchResult[] | null,
    rowId: number | null,
    originalRowId: number | null,
  ) => MatchElement[];
  isMatchIconOnly: boolean;
  showDetailButton: boolean;
  handleDetailClick: () => void;
}

const ValidationStatusCell: React.FC<ValidationStatusCellProps> = ({ cell, handleDuplicatesButtonPress, duplicatesCurrentlyHidden, warningsCurrentlyHidden, noticesCurrentlyHidden, isTrade, deleteMatch, updateMatch, forceMatch, getSubRowPossibleMatches, isMatchIconOnly, showDetailButton, handleDetailClick }) => {
  const { t } = useTranslation();
  const value: MatchElement[] = cell.getValue() || [];
  const { userSettings } = useContext(StoreContext);
  const userId = userSettings?.userId;

  const getMatchPossibilitiesIcons = (
    row: ValidationTableDef | null,
    possibleMatches: ValidationTableMatchResult[] | null,
    rowId: number | null,
  ): React.ReactElement[] => {
  if (!possibleMatches || possibleMatches == null || possibleMatches.length === 0) {
    //is matched, but possible_matches not populated
    if (row && row.transaction && row.transaction.id && row.matched_transaction) {
      return [<StatusIcon type={StatusEnum.SUCCESS} modifiers={[StatusIconModifier.VALIDATION]} />];
    }
    return [];
  }

  if (!rowId || rowId == null) return [];

  let hasMatch = false;

  for (const [key, value] of Object.entries(possibleMatches)) {
    const transactionId: number | undefined = (value.transaction_id as
          number) | (value.transaction?.id as
          number);
    if (!transactionId || transactionId == null) {
      continue;
    }

    if (value.match_possibility && value.match_possibility >= 90) {
      return [<StatusIcon type={StatusEnum.SUCCESS} modifiers={[StatusIconModifier.VALIDATION]} />];
    } else {
      hasMatch = true;
    }
  }

  if (hasMatch) {
      return [<StatusIcon type={StatusEnum.SUCCESS} modifiers={[StatusIconModifier.VALIDATION]} />];
  }

  return [];
};

  if (!userId) return null;

  return (
    <div className="ct_validationStatusCell">
      <div>
      { !cell.row.parentId && (//Parent
        <>
          { !!cell.row.original.transaction.matched_transaction_id
            && cell.row.original.transaction.id 
            && getMatchPossibilitiesIcons(
                cell.row.original,
                cell.row.original.possible_matches,
                cell.row.original.transaction.id,
              )
              .map((value) => (
                <span
              key={`parent_match_${cell.row.original.transaction.matched_transaction_id}`}
              data-match-id={cell.row.original.transaction.matched_transaction_id}
              data-pair-id={cell.row.original.transaction.id}
            >
              <span style={{ marginRight: '4px', position: 'relative', top: '4px' }}>
                <Pill
                  key={`parent_auto_match_span_${cell.row.original.transaction.id}`}
                  value={(cell.row.original.transaction.is_auto_match_blocked || false)
                    ? t('Manual', { ns: 'validation' })
                    : t('Automatic', { ns: 'validation' })}
                  modifiers={[PillModifier.NEUTRAL]}
                />
              </span>
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  cell.row.toggleExpanded();
                }}
                type="button"
                className="ct_btn _noPadding _smallHeight"
                style={{ marginRight: '4px', position: 'relative', top: '4px' }}
              >
                {value}
              </button>
            </span>              
            )
          )}

          { !!cell.getValue<MatchElement[]>()
            && cell.getValue<MatchElement[]>()?.length > 0
            && cell.row.original.transaction.matched_transaction_id == null
            && cell.getValue<MatchElement[]>()
              .sort((
                value: MatchElement,
                value2: MatchElement,
              ) => (value.color.includes(PillModifier.BLUE) ? -1 : 1))
              .slice(0, 1)//only return 1 element
              .map((value: MatchElement, index: number) => (
                <Tooltip
                    placement="top-start"
                    arrow
                    disableInteractive
                    title={t('Show Matches', { ns: 'validation' })}
                    key={`parent_show_matches_${cell.row.original.transaction.id}_${index}`}
                  >
                <span
                  key={`parent_match_span_${value.rowId}_${index}_${value.transactionId}`}
                  data-id={value.transactionId}
                >
                  <button
                    key={`parent_match_button_${value.rowId}_${index}_${value.transactionId}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      cell.row.toggleExpanded();
                    }}
                    type="button"
                    className="ct_btn _noPadding _smallHeight"
                    style={{ marginRight: '4px', position: 'relative', top: '4px' }}
                  >
                    {!isMatchIconOnly && (
                      <Pill
                      key={`parent_show_matches_pill_${index}_${value.transactionId}`}
                      value={isTrade(cell.row.original)
                        ? t('Check Match', { ns: 'validation' })
                        : t('Check Matches', { ns: 'validation' })}
                      modifiers={value.color}
                      />
                    )}
                    {isMatchIconOnly && (
                      <StatusIcon 
                      key={`parent_show_matches_status_${cell.row.original.transaction.id}_${index}`}
                      type={StatusEnum.GRAY_OUTLINE}
                      modifiers={[StatusIconModifier.VALIDATION]} />
                    )}
                  </button>
                </span>
                </Tooltip>
              ))}

          {!!cell.row.original.report 
            && cell.row.original.report.is_duplicate
            && cell.row.original.report.is_duplicate === true
            && !duplicatesCurrentlyHidden
            && (
              <div
                key={`parent_duplicate_div_${cell.row.original.transaction.id}`}
              >
                <Btn
                  key={`parent_duplicate_btn_${cell.row.original.transaction.id}`}
                  onClick={(event) => {
                    if (event) {
                      event.stopPropagation();
                    }
                    handleDuplicatesButtonPress(cell.row.original.transaction.id);
                  }}
                  type="link"
                  text=""
                  icon={
                    <Pill
                      key={`parent_duplicate_pill_inner${cell.row.original.transaction.id}`}
                      value={t('Duplicate', { ns: 'validation' })}
                      modifiers={[PillModifier.GREEN]}
                    />
                  }
                  modifier={[]}
                />
              </div>
            )}

          {!!cell.row.original 
            && cell.row.original.warnings
            && !warningsCurrentlyHidden
            && hasItems(cell.row.original.warnings)
            && (
              <ValidationStatusTooltip
                key={`parent_warnings_tooltip_inner${cell.row.original.transaction.id}`}
                warnings={cell.row.original.warnings}
                notices={cell.row.original.notices}
                show={ValidationStatusTooltipKind.WARNINGS}
              />
            )}
  
            {!!cell.row.original 
              && cell.row.original.notices
              && !noticesCurrentlyHidden
              && hasItems(cell.row.original.notices)
              && (
              <ValidationStatusTooltip
                key={`parent_notices_tooltip_inner${cell.row.original.transaction.id}`}
                warnings={cell.row.original.warnings}
                notices={cell.row.original.notices}
                show={ValidationStatusTooltipKind.NOTICES}
              />
              )}
          </>
        )}

      { cell.row.parentId && (// Child row
                  <>
      { !!cell.row.original.transaction.matched_transaction_id //Matched
      && cell.row.original.transaction.id
      && (cell.row.getParentRow()?.original.matched_transaction || null) && (//Matched to parent
      <span
        key={`child_match_inner_${cell.row.original.transaction.matched_transaction_id}`}
        data-match-id={cell.row.original.transaction.matched_transaction_id}
        data-pair-id={cell.row.original.transaction.id}
      >
        <button
          onClick={(event) => {
            event.stopPropagation();
            deleteMatch(
              null,
              cell.row.original.transaction.id,
              userId,
              getCoinString(cell.row.original.transaction),
              cell.row.original.transaction.transaction_time,
              cell.row.getParentRow()?.original.matched_transaction.transaction_time || -1,
            );
          }}
          type="button"
          className="ct_btn _link _old _noPadding _smallHeight"
        >
          <Pill
            key={`child_del_button_inner_${cell.row.original.transaction.id}`}
            value={t('Unmatch', { ns: 'validation' })}
            modifiers={[PillModifier.ERROR]}
          />
        </button>
      </span>
              )}

    { !!cell.row.original.transaction.matched_transaction_id //Matched to another transaction
      && cell.row.original.transaction.id 
      && !cell.row.getParentRow()?.original.transaction.matched_transaction_id //Not matched to parent
      && (
      <span
        key={`child_match_inner_another_${cell.row.original.transaction.matched_transaction_id}`}
        data-match-id={cell.row.original.transaction.matched_transaction_id}
        data-pair-id={cell.row.original.transaction.id}
      >
        <button
          onClick={(event) => {
            event.stopPropagation();
            forceMatch(
              cell.row.getParentRow()?.original.transaction.id,
              cell.row.original.transaction.id,
              cell.row.original.transaction.id,
              userId,
              getCoinString(cell.row.original.transaction),
              cell.row.original.transaction.transaction_time,
              cell.row.getParentRow()?.original.transaction.transaction_time || -1,
            );
          }}
          type="button"
          className="ct_btn _link _old _noPadding _smallHeight"
        >
          <Pill
            key={`child_del_button_inner_another_${cell.row.original.transaction.id}`}
            value={t('Match', { ns: 'validation' })}
            modifiers={[PillModifier.ERROR]}
          />
        </button>
      </span>
              )}

              { !!getSubRowPossibleMatches(
                cell.row.getParentRow()?.original.possible_matches || null,
                cell.row.original.transaction.id,
                cell.row.getParentRow()?.original.transaction.id || null,
              )
            && getSubRowPossibleMatches(
              cell.row.getParentRow()?.original.possible_matches || null,
              cell.row.original.transaction.id,
              cell.row.getParentRow()?.original.transaction.id || null,
            )?.length > 0
            && cell.row.original.transaction.matched_transaction_id == null
            && getSubRowPossibleMatches(
              cell.row.getParentRow()?.original.possible_matches || null,
              cell.row.original.transaction.id,
              cell.row.getParentRow()?.original.transaction.id || null,
            )
            .slice(0, 1)//only return 1 element
            .map((value, index) => (
              <span
                key={`child_match_span_2_${value.rowId}_${index}_${value.transactionId}`}
                data-id={value.transactionId}
              >
                <button
                  onClick={(event) => {
                    event.stopPropagation();
                    updateMatch(
                      value.rowId,
                      value.transactionId,
                      userId,
                      value.coin || "",
                      value.transaction_time || -1,
                      cell.row.original.transaction.transaction_time
                    );
                  }}
                  type="button"
                  key={`match_button_2_${value.rowId}_${index}_${value.transactionId}`}
                  className="ct_btn _link _old _noPadding _smallHeight"
                >
                  <Pill
                    key={`child_check_match_${index}_${value.transactionId}`}
                    value={t('Match', { ns: 'validation' })}
                    modifiers={value.color}
                  />
                </button>
              </span>
            ))}

              {!!cell.row.original.report
    && cell.row.original.report.is_duplicate
    && cell.row.original.report.is_duplicate === true
    && !duplicatesCurrentlyHidden
    && (
      <span
        key={`child_duplicate_span_inner_${cell.row.original.transaction.id}`}
      >
        <Btn
          key={`child_duplicate_btn_inner_${cell.row.original.transaction.id}`}
          onClick={(event) => {
            if (event) {
              event.stopPropagation();
            }
            handleDuplicatesButtonPress(cell.row.original.transaction.id);
          }}
          type="link"
          text=""
          icon={
          <Pill
            key={`child_duplicate_pill_inner${cell.row.original.transaction.id}`}
            value={t('Duplicate', { ns: 'validation' })}
            modifiers={[PillModifier.GREEN]}
          />
          }
          modifier={[]}
        />
      </span>
    )}

          {!!cell.row.original 
            && cell.row.original.warnings
            && !warningsCurrentlyHidden
            && hasItems(cell.row.original.warnings)
            && (
              <ValidationStatusTooltip
                key={`child_warnings_tooltip_inner${cell.row.original.transaction.id}`}
                warnings={cell.row.original.warnings}
                notices={cell.row.original.notices}
                show={ValidationStatusTooltipKind.WARNINGS}
              />
            )}
  
            {!!cell.row.original 
              && cell.row.original.notices
              && !noticesCurrentlyHidden
              && hasItems(cell.row.original.notices)
              && (
              <ValidationStatusTooltip
                key={`child_notices_tooltip_inner${cell.row.original.transaction.id}`}
                warnings={cell.row.original.warnings}
                notices={cell.row.original.notices}
                show={ValidationStatusTooltipKind.NOTICES}
              />
              )}
            </>
            )}
       </div>
              <div
                key={`detail_open_btn_${cell.row.original.transaction.id}`}
                onClick={(event) => {
                  event.stopPropagation();
                  handleDetailClick();
                }}
                style={{ 
                  cursor: 'pointer',
                  top: '3px',
                  position: 'relative'
                }}
              >
                {showDetailButton && (
                <ArrowRightIcon 
                style={{ 
                  zoom: 0.8,
                }}
                />
                )}
            </div>
          </div>
    );
  };
  
  export default ValidationStatusCell;